import React from "react";

/* ---------------- VALUE MAPS ---------------- */
const APPROVAL_STATUS_MAP = {
  0: "Pending",
  1: "Approved",
  2: "Rejected",
};

/* ---------------- SKIP KEYS ---------------- */
const SKIP_KEYS = [
  "rejected_by",
  "approved_by",
  "admin_approval_status",
];

/* ---------------- HELPERS ---------------- */
const safeBase64Decode = (value) => {
  try {
    return atob(value);
  } catch {
    return value;
  }
};

const isEmpty = (val) =>
  val === null ||
  val === undefined ||
  String(val).trim() === "";

/* ---------------- GET ONLY CHANGED KEYS ---------------- */
const getChangedData = (before = {}, after = {}) => {
  const changedBefore = {};
  const changedAfter = {};

  Object.keys(before).forEach((key) => {
    const normalizedKey = key.toLowerCase();

    if (
      ["updated_at", "password"].includes(normalizedKey) ||
      SKIP_KEYS.includes(normalizedKey)
    ) {
      return;
    }

    const beforeVal = before[key];
    const afterVal = after[key];

    if (isEmpty(beforeVal) && isEmpty(afterVal)) return;

    if (String(beforeVal) !== String(afterVal)) {
      changedBefore[key] = beforeVal;
      changedAfter[key] = afterVal;
    }
  });

  return { changedBefore, changedAfter };
};

/* ---------------- TABLE RENDER ---------------- */
const renderDataTable = (data) => {
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="alert alert-secondary text-center mb-0 small">
        No changes
      </div>
    );
  }

  return (
    <table className="table table-sm table-bordered align-middle mb-0">
      <thead className="table-light">
        <tr>
          <th style={{ width: "40%" }}>Name</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(data).map(([key, value]) => {
          const formattedKey = key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());

          const normalizedKey = key
            .toLowerCase()
            .replace(/_/g, " ");

          let displayValue = value;

          if (isEmpty(displayValue)) {
            displayValue = "";
          }

          if (normalizedKey === "email address" && displayValue) {
            displayValue = safeBase64Decode(String(displayValue));
          }

          if (normalizedKey === "approval status" && displayValue !== "") {
            displayValue =
              APPROVAL_STATUS_MAP[displayValue] ?? displayValue;
          }

          return (
            <tr key={key}>
              <td className="fw-semibold text-muted">
                {formattedKey}
              </td>
              <td className="text-dark">
                {String(displayValue) || (
                  <span className="text-muted fst-italic">
                    empty
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/* ---------------- COMPARE MODAL ---------------- */
function Compare({ show, row, onClose }) {
  if (!show) return null;

  const { changedBefore, changedAfter } = getChangedData(
    row?.before_data,
    row?.after_data
  );

  return (
    <>
      <div className="modal fade show d-block">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content border-0 shadow">
            <div className="modal-header bg-dark text-white">
              <h5 className="modal-title fw-semibold">
                Data Comparison
              </h5>
              <button
                className="btn-close btn-close-white"
                onClick={onClose}
              />
            </div>

            <div className="modal-body">
              <div className="row g-4">
                {/* BEFORE */}
                <div className="col-md-6">
                  <div className="card h-100 border-danger-subtle">
                    <div className="card-header bg-danger-subtle text-danger fw-semibold">
                      Before Data
                    </div>
                    <div className="card-body p-2 bg-light">
                      {renderDataTable(changedBefore)}
                    </div>
                  </div>
                </div>

                {/* AFTER */}
                <div className="col-md-6">
                  <div className="card h-100 border-success-subtle">
                    <div className="card-header bg-success-subtle text-success fw-semibold">
                      After Data
                    </div>
                    <div className="card-body p-2 bg-white">
                      {renderDataTable(changedAfter)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer bg-light">
              <button
                className="btn btn-secondary btn-sm px-4"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show"></div>
    </>
  );
}

export default Compare;
